'use client';

// ---------------------------------------------------------------------------
// MusicAudioEngine — the one <audio> element, and everything imperative
// ---------------------------------------------------------------------------
//
// Rendered from MusicProvider, which lives in providers.tsx — the only node React
// never remounts. That is what makes playback survive navigation: each route
// group's layout (and its Topbar) unmounts when you cross from /dashboard to
// /admin, so an element owned by any of them would stop the music.
//
// It renders no UI. The store says what should be happening; this translates that
// into calls on the element, and the element's events back into the store.
//
// Four things worth knowing before editing:
//
//   `play()` returns a PROMISE that rejects. Before the user has interacted with
//   the page the browser refuses it with NotAllowedError, so the rejection has to
//   be caught and surfaced as "press play" — an uncaught one leaves the store
//   saying 'playing' over silence.
//
//   A local track's source is an object URL that must be revoked when we move on,
//   or every track played in a shift stays pinned in memory.
//
//   Source resolution is async and races. Two quick track changes can settle out
//   of order, so each resolve is tagged and a stale one is discarded — otherwise
//   the element ends up loading the track you skipped past.
//
//   An unplayable local file must SKIP, not stall. A missing file that simply
//   stopped the queue would look like the player had frozen.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef } from 'react';

import { useToast } from '@/core/hooks/useToast';
import { useTranslation } from '@/core/i18n';

import { releaseSource, resolveTrackSource } from '../lib/resolve-source';
import { isLocalTrack } from '../lib/track';
import { selectCurrentTrack, useMusicStore } from '../stores/music.store';

export function MusicAudioEngine() {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  /** The object URL currently loaded, so it can be revoked on change. */
  const loadedUrlRef = useRef<{ url: string; isObjectUrl: boolean } | null>(null);

  /** Increments on every track change; a resolve tagged with an older value is stale. */
  const loadGenerationRef = useRef(0);

  /**
   * The track whose source is actually on the element. Differs from the store's
   * current track while a new source is resolving — and `play()` in that window
   * would start the PREVIOUS track, which the incoming `load()` then pauses.
   */
  const loadedTrackIdRef = useRef<string | null>(null);

  /**
   * Set when we call `load()` on an element that is playing. The spec makes that
   * `load()` fire a `pause` event; it is ours, not the user's, and must not flip
   * the store to 'paused' — that would stop the track we are switching to.
   */
  const ignoreNextPauseRef = useRef(false);

  const { t } = useTranslation();
  const { toast } = useToast();

  const currentTrack = useMusicStore(selectCurrentTrack);
  const status = useMusicStore((s) => s.status);
  const volume = useMusicStore((s) => s.volume);
  const muted = useMusicStore((s) => s.muted);
  const pendingSeekSec = useMusicStore((s) => s.pendingSeekSec);
  const userHasInteracted = useMusicStore((s) => s.userHasInteracted);

  const trackKey = currentTrack?.id ?? null;

  const releaseLoaded = useCallback(() => {
    if (loadedUrlRef.current) {
      releaseSource(loadedUrlRef.current.url, loadedUrlRef.current.isObjectUrl);
      loadedUrlRef.current = null;
    }
  }, []);

  /** `load()` that does not let its own `pause` event reach the store. */
  const reloadElement = useCallback((el: HTMLAudioElement) => {
    if (!el.paused) ignoreNextPauseRef.current = true;
    el.load();
  }, []);

  /**
   * Start the loaded source if the store wants playback. Called from both the
   * status effect and the loader, because whichever settles second must start it.
   */
  const playIfWanted = useCallback(
    (el: HTMLAudioElement) => {
      const { status } = useMusicStore.getState();
      if (status !== 'playing' && status !== 'loading') return;
      const attempt = el.play();
      // Older browsers return undefined rather than a promise.
      if (!attempt || typeof attempt.catch !== 'function') return;
      attempt.catch((err: unknown) => {
        const store = useMusicStore.getState();
        const name = (err as { name?: string } | null)?.name;
        if (name === 'NotAllowedError') {
          // No user gesture yet. This is the autoplay policy, not a fault:
          // ask for a press instead of claiming to be playing over silence.
          store.reportStatus('paused');
          if (!store.userHasInteracted) store.setScheduleAwaitingGesture(store.activeScheduleId);
          return;
        }
        if (name === 'AbortError') {
          // A newer load() superseded this play(). Expected while skipping.
          return;
        }
        store.reportStatus('paused');
        store.reportError(t('music.errors.playbackFailed'));
      });
    },
    [t],
  );

  // ---- Load the current track ---------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const generation = ++loadGenerationRef.current;

    if (!trackKey || !currentTrack) {
      releaseLoaded();
      loadedTrackIdRef.current = null;
      el.removeAttribute('src');
      reloadElement(el);

      // A position IS selected but nothing here can describe it. That happens
      // with a playlist carrying a local file from another machine: the id is in
      // the queue, but this browser's IndexedDB has no entry for it.
      //
      // Clearing src and stopping was the original behaviour, and it presented
      // as the player freezing on silence — the exact failure the skip logic
      // exists to prevent. `nextPlayableIndex` already treats an unknown id as
      // unplayable, so advancing walks past it and terminates if nothing is
      // playable.
      const store = useMusicStore.getState();
      if (store.currentIndex >= 0 && store.currentIndex < store.queue.length) {
        store.next(true);
      }
      return;
    }

    let cancelled = false;

    void (async () => {
      const result = await resolveTrackSource(currentTrack);

      // A newer track change already started, or the effect was torn down.
      if (cancelled || generation !== loadGenerationRef.current) {
        if (result.ok) releaseSource(result.url, result.isObjectUrl);
        return;
      }

      const store = useMusicStore.getState();

      if (!result.ok) {
        if (isLocalTrack(currentTrack)) store.setLocalStatus(currentTrack.id, result.reason);

        store.reportError(
          result.reason === 'needs-permission'
            ? t('music.errors.needsPermission', { title: currentTrack.title })
            : t('music.errors.fileMissing', { title: currentTrack.title }),
        );

        // Skip rather than stall. A stopped queue reads as a frozen player.
        store.next(true);
        return;
      }

      releaseLoaded();
      loadedUrlRef.current = { url: result.url, isObjectUrl: result.isObjectUrl };

      el.src = result.url;
      reloadElement(el);
      loadedTrackIdRef.current = currentTrack.id;
      // The status effect skipped play() while this source was resolving.
      playIfWanted(el);

      if (isLocalTrack(currentTrack)) store.setLocalStatus(currentTrack.id, 'ok');
      store.reportError(null);
      if (result.correctedDurationSec !== undefined) {
        store.reportDuration(result.correctedDurationSec);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `currentTrack` is intentionally keyed on its id: re-resolving because an
    // unrelated field changed would restart playback mid-track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackKey, releaseLoaded, reloadElement, playIfWanted, t]);

  // Revoke the last object URL when the engine itself goes away.
  useEffect(() => releaseLoaded, [releaseLoaded]);

  // ---- Volume -------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = volume;
    el.muted = muted;
  }, [volume, muted]);

  // ---- Play / pause -------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    if (status === 'playing' || status === 'loading') {
      // The element still holds the previous track while the new source
      // resolves; the loader starts playback once it lands.
      if (loadedTrackIdRef.current !== trackKey) return;
      playIfWanted(el);
    } else if (status === 'paused' || status === 'idle') {
      el.pause();
    }
  }, [status, trackKey, userHasInteracted, playIfWanted]);

  // ---- Seek ---------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el || pendingSeekSec === null) return;
    const target = useMusicStore.getState().consumeSeek();
    if (target === null) return;
    try {
      el.currentTime = target;
    } catch {
      // Seeking before metadata is known throws. The position is already in the
      // store, and the next loadedmetadata will land close enough.
    }
  }, [pendingSeekSec]);

  // ---- Element events -----------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const store = () => useMusicStore.getState();

    const onTimeUpdate = () => store().reportPosition(el.currentTime);
    const onDurationChange = () =>
      store().reportDuration(Number.isFinite(el.duration) ? el.duration : null);
    const onPlaying = () => store().reportStatus('playing');
    const onPause = () => {
      // Ignore the pause our own load() fires while switching source — it
      // would stop the incoming track, not just flicker the UI.
      if (ignoreNextPauseRef.current) {
        ignoreNextPauseRef.current = false;
        return;
      }
      if (!el.ended && store().status === 'playing') store().reportStatus('paused');
    };
    // Any pause our load() caused has been dispatched by now. Not every engine
    // fires one, so clear the flag here or it would swallow the user's next pause.
    const onLoadStart = () => {
      ignoreNextPauseRef.current = false;
    };
    const onEnded = () => store().next(true);
    const onError = () => {
      const track = selectCurrentTrack(store());
      store().reportError(
        t('music.errors.cannotPlay', { title: track?.title ?? t('music.unknownTrack') }),
      );
      // One bad file must not end the session.
      store().next(true);
    };
    const onWaiting = () => {
      if (store().status === 'playing') store().reportStatus('loading');
    };

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('loadedmetadata', onDurationChange);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('pause', onPause);
    el.addEventListener('loadstart', onLoadStart);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    el.addEventListener('waiting', onWaiting);

    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('loadedmetadata', onDurationChange);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('loadstart', onLoadStart);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
      el.removeEventListener('waiting', onWaiting);
    };
  }, [t]);

  // ---- Surface an error once, rather than on every render -----------------
  const lastError = useMusicStore((s) => s.lastError);
  const shownErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lastError || lastError === shownErrorRef.current) return;
    shownErrorRef.current = lastError;
    toast('warning', t('music.title'), lastError);
  }, [lastError, toast, t]);

  // ---- OS media keys and lock-screen metadata -----------------------------
  //
  // Cheap, and it is what makes the player feel native: headset buttons and the
  // media keys on a front-desk keyboard work, and the track shows on the lock
  // screen. Guarded because Safari and Firefox lack parts of it.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;
    if (!currentTrack) {
      session.metadata = null;
      return;
    }
    try {
      session.metadata = new MediaMetadata({
        title: currentTrack.title,
        artist: currentTrack.artist ?? '',
        album: isLocalTrack(currentTrack) ? t('music.deviceOnly') : (currentTrack.album ?? ''),
      });
    } catch {
      // MediaMetadata missing. The action handlers below still work.
    }
  }, [currentTrack, t]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;
    const store = () => useMusicStore.getState();

    const handlers: [MediaSessionAction, () => void][] = [
      ['play', () => store().play()],
      ['pause', () => store().pause()],
      ['nexttrack', () => store().next(false)],
      ['previoustrack', () => store().previous()],
      ['stop', () => store().pause()],
    ];

    for (const [action, handler] of handlers) {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // Unsupported action on this browser — skip it, keep the rest.
      }
    }

    return () => {
      for (const [action] of handlers) {
        try {
          session.setActionHandler(action, null);
        } catch {
          // Nothing to clear.
        }
      }
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState =
      status === 'playing' ? 'playing' : status === 'idle' ? 'none' : 'paused';
  }, [status]);

  return (
    <audio
      ref={audioRef}
      id="music-audio-element"
      // `metadata` rather than `auto`: a front desk on a metered or slow link
      // should not prefetch a whole queue's audio before anyone presses play.
      preload="metadata"
      // No `controls` — the UI is ours. Kept in the tree and never unmounted.
      className="hidden"
      aria-hidden="true"
    />
  );
}
